import { utils, WorkSheet } from 'xlsx';
import { ExcelTable } from './table';

const DEFAULT_EMPTY_LINES_COUNT_BETWEEN_TABLES = 1;

export class ExcelWorkSheet {
  private worksheet: WorkSheet;
  private worksheetNextEmptyLineIndex: number;
  constructor(private _name: string, table?: ExcelTable) {
    this.worksheet = utils.aoa_to_sheet([]);
    this.worksheetNextEmptyLineIndex = 0;
    this.worksheet['!merges'] = [];
    this.worksheet['!cols'] = [];

    if (table) {
      this.appendTable(table, { skipLines: 0 });
    }
  }

  public get name() {
    return this._name;
  }

  public get underlyingWorksheet() {
    return this.worksheet;
  }

  public appendTable(
    table: ExcelTable,
    options: {
      skipLines?: number;
    } = {}
  ) {
    const skipLines =
      typeof options.skipLines === 'number'
        ? options.skipLines
        : DEFAULT_EMPTY_LINES_COUNT_BETWEEN_TABLES;
    const tableOrigin = {
      r: this.worksheetNextEmptyLineIndex + skipLines,
      c: 0
    };

    utils.sheet_add_aoa(this.worksheet, table.getValues(), {
      origin: tableOrigin
    });

    this.mergeCells(table, tableOrigin);

    this.applyFormattingToCells(table, tableOrigin);
    this.adjustExcelColumnsWidth(table, tableOrigin);
    this.worksheetNextEmptyLineIndex += skipLines + table.rowsCount;
  }

  private mergeCells(table: ExcelTable, origin: { r: number; c: number }) {
    const cellsColSpan = table.getColSpan();
    const merges = [];
    for (let rowI = 0; rowI < table.rowsCount; rowI++) {
      for (let colI = 0; colI < table.colsCount; colI++) {
        if (!cellsColSpan[rowI][colI] || cellsColSpan[rowI][colI] === 1)
          continue;
        merges.push({
          s: { c: colI + origin.c, r: rowI + origin.r },
          e: {
            c: colI + origin.c + cellsColSpan[rowI][colI] - 1,
            r: rowI + origin.r
          }
        });
      }
    }

    this.worksheet['!merges'] = this.worksheet['!merges'].concat(merges);
  }

  private applyFormattingToCells(
    table: ExcelTable,
    origin: { r: number; c: number }
  ) {
    const cellsFormattingTable = table.getFormatting();
    for (let rowI = 0; rowI < table.rowsCount; rowI++) {
      for (let colI = 0; colI < table.colsCount; colI++) {
        const cell = this.worksheet[
          utils.encode_cell({ r: rowI + origin.r, c: colI + origin.c })
        ];
        if (!cell) continue;
        if (cellsFormattingTable[rowI][colI] !== null)
          cell.z = cellsFormattingTable[rowI][colI];
        utils.format_cell(cell);
      }
    }
  }

  private adjustExcelColumnsWidth(
    table: ExcelTable,
    origin: { r: number; c: number }
  ) {
    const prevColumnsConfig = this.worksheet['!cols'];
    const formattedTable = this.getFormattedValues(
      table.rowsCount,
      table.colsCount,
      origin
    );
    const requiredColumnsWidthForTable = this.getMinRequiredWidthForColumns(
      table,
      formattedTable
    );

    const filledColumnsCount = Math.max(
      prevColumnsConfig.length,
      table.colsCount
    );

    const newColumnsWidth = new Array(filledColumnsCount).fill(0);

    for (let colI = 0; colI < filledColumnsCount; colI++) {
      newColumnsWidth[colI] = Math.max(
        (prevColumnsConfig[colI] && prevColumnsConfig[colI].wch) || 0,
        requiredColumnsWidthForTable[colI] || 0
      );
    }

    this.worksheet['!cols'] = newColumnsWidth.map((width) =>
      width > 0 ? { wch: width } : undefined
    );
  }

  private getFormattedValues(
    rowsCount: number,
    colsCount: number,
    origin: { r: number; c: number }
  ) {
    const formattedValues = [];
    for (let rowI = 0; rowI < rowsCount; rowI++) {
      const row = [];
      for (let colI = 0; colI < colsCount; colI++) {
        const cell = this.worksheet[
          utils.encode_cell({ r: rowI + origin.r, c: colI + origin.c })
        ];
        row[colI] = cell && cell.w;
      }
      formattedValues.push(row);
    }
    return formattedValues;
  }

  private getMinRequiredWidthForColumns(
    table: ExcelTable,
    tableFormattedValues: string[][]
  ): number[] {
    return tableFormattedValues.reduce((colsWidth: number[], row: string[]) => {
      row.forEach((value: string, colI: number) => {
        if (
          (table.exportConfig.shouldBeFullyVisible ||
            (table.exportConfig.columnsConfig &&
              table.exportConfig.columnsConfig[colI].shouldBeFullyVisible)) &&
          !!value
        ) {
          if (!colsWidth[colI] || colsWidth[colI] < value.length) {
            colsWidth[colI] = value.length;
          }
        }
      });
      return colsWidth;
    }, new Array(table.colsCount).fill(0));
  }
}
